import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS, apiUtils } from '../config/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  Divider,
  Switch,
  Checkbox,
} from '@mui/material';
import {
  Add,
  Person,
  Assignment,
  CheckCircle,
  Email,
  Badge,
  Assessment as AssessmentIcon,
  Business,
  PlayArrow,
  Search,
  FilterList,
  Sort,
  VolumeUp,
  MenuBook,
  Mic,
} from '@mui/icons-material';

interface Talent {
  id: string;
  name: string;
  email: string;
  status: string;
  created_at: string;
}

interface AssessmentSection {
  section_name: string;
  duration_minutes: number;
  total_score: number;
  question_selection: {
    [key: string]: number;
  };
}

interface Assessment {
  id: string;
  name: string;
  total_duration_minutes: number;
  total_score: number;
  configuration?: {
    total_duration_minutes?: number;
    total_score?: number;
    sections?: AssessmentSection[];
  };
}

interface TenantAccess {
  id: string;
  tenant_name: string;
  tenant_email: string;
}

interface Section {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  instructions?: {
    section_instructions?: string;
    duration_minutes?: number;
    total_score?: number;
    question_types?: string[];
  };
  language_id: number;
}

interface SubSection {
  id: string;
  section_id: string;
  name: string;
  description?: string;
  is_active: boolean;
}

interface SectionConfig {
  sectionId: string;
  enabled: boolean;
  subsections: {
    [subSectionId: string]: {
      enabled: boolean;
      questionCount: number;
      score: number;
    };
  };
}

const Dashboard: React.FC = () => {
  const [assignAssessmentOpen, setAssignAssessmentOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [savedTalent, setSavedTalent] = useState<Talent | null>(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [talents, setTalents] = useState<Talent[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [tenantAccess, setTenantAccess] = useState<TenantAccess[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [subsections, setSubsections] = useState<SubSection[]>([]);
  const [sectionConfigs, setSectionConfigs] = useState<{[sectionId: string]: SectionConfig}>({});

  const [assignmentData, setAssignmentData] = useState({
    talentName: '',
    talentEmail: '',
    selectedTenantId: '',
    assessmentType: 'existing',
    selectedAssessmentId: '',
    newAssessmentName: '',
    newAssessmentDescription: '',
    newAssessmentDuration: 15,
    newAssessmentScore: 100,
  });

  const steps = ['Add Talent', 'Assign Assessment'];

  // Helper functions for section configuration
  const updateSectionEnabled = (sectionId: string, enabled: boolean) => {
    setSectionConfigs(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        enabled
      }
    }));
  };

  const updateSubsectionConfig = (sectionId: string, subsectionId: string, field: 'enabled' | 'questionCount' | 'score', value: boolean | number) => {
    setSectionConfigs(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        subsections: {
          ...prev[sectionId].subsections,
          [subsectionId]: {
            ...prev[sectionId].subsections[subsectionId],
            [field]: value
          }
        }
      }
    }));
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load talents (assignments)
      const talentsData = await apiUtils.get(API_ENDPOINTS.ASSIGNMENTS);
      setTalents(talentsData.data || []);

      // Load assessments
      const assessmentsData = await apiUtils.get(API_ENDPOINTS.ASSESSMENTS);
      setAssessments(assessmentsData.data || []);

      // Load tenant access
      const tenantData = await apiUtils.get(API_ENDPOINTS.TENANT_ACCESS);
      setTenantAccess(tenantData.data || []);

      // Load sections
      const sectionsData = await apiUtils.get(API_ENDPOINTS.SECTIONS);
      const activeSections = (sectionsData.data || []).filter((section: Section) => section.is_active);
      setSections(activeSections);
      console.log('Loaded sections:', activeSections);

      // Load subsections
      const subsectionsData = await apiUtils.get(API_ENDPOINTS.SUBSECTIONS);
      const activeSubsections = (subsectionsData.data || []).filter((subsection: SubSection) => subsection.is_active);
      setSubsections(activeSubsections);
      console.log('Loaded subsections:', activeSubsections);

      // Initialize section configurations based on section instructions
      const initialConfigs: {[sectionId: string]: SectionConfig} = {};
      activeSections.forEach((section: Section) => {
        const sectionSubsections = activeSubsections.filter((sub: SubSection) => sub.section_id === section.id);
        const subsectionConfigs: {[subSectionId: string]: {enabled: boolean; questionCount: number; score: number}} = {};
        
        // Get default values from section instructions
        const sectionInstructions = section.instructions || {};
        const defaultScore = sectionInstructions.total_score || 30;
        const questionTypes = sectionInstructions.question_types || [];
        
        if (sectionSubsections.length > 0) {
          // Use actual subsections
          sectionSubsections.forEach((subsection: SubSection) => {
            subsectionConfigs[subsection.id] = {
              enabled: true,
              questionCount: 1,
              score: Math.floor(defaultScore / sectionSubsections.length)
            };
          });
        } else {
          // Create virtual subsections based on question_types
          questionTypes.forEach((questionType: string, index: number) => {
            const virtualId = `${section.id}_${questionType}`;
            subsectionConfigs[virtualId] = {
              enabled: true,
              questionCount: 1,
              score: Math.floor(defaultScore / questionTypes.length)
            };
          });
        }
        
        initialConfigs[section.id] = {
          sectionId: section.id,
          enabled: true,
          subsections: subsectionConfigs
        };
      });
      
      setSectionConfigs(initialConfigs);
      console.log('Initialized section configs:', initialConfigs);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleOpenDialog = () => {
    setAssignAssessmentOpen(true);
    setActiveStep(0);
    setSavedTalent(null);
    setAssignmentData({
      talentName: '',
      talentEmail: '',
      selectedTenantId: '',
      assessmentType: 'existing',
      selectedAssessmentId: '',
      newAssessmentName: '',
      newAssessmentDescription: '',
      newAssessmentDuration: 15,
      newAssessmentScore: 100,
    });
  };

  const handleCloseDialogs = () => {
    setAssignAssessmentOpen(false);
    setActiveStep(0);
    setSavedTalent(null);
  };

  const handleAddTalent = async () => {
    if (!assignmentData.talentName || !assignmentData.talentEmail) {
      alert('Please fill in all talent information');
      return;
    }

    try {
      setAssignmentLoading(true);
      
      // Create a mock talent for now
      const newTalent: Talent = {
        id: Date.now().toString(),
        name: assignmentData.talentName,
        email: assignmentData.talentEmail,
        status: 'pending',
        created_at: new Date().toISOString(),
      };

      setSavedTalent(newTalent);
      setActiveStep(1);
    } catch (error) {
      console.error('Error adding talent:', error);
      alert('Error adding talent');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAssignAssessment = async () => {
    if (!savedTalent) return;

    if (assignmentData.assessmentType === 'existing' && !assignmentData.selectedAssessmentId) {
      alert('Please select an assessment');
      return;
    }

    if (assignmentData.assessmentType === 'new' && (!assignmentData.newAssessmentName || !assignmentData.newAssessmentDescription)) {
      alert('Please fill in all assessment information');
      return;
    }

    try {
      setAssignmentLoading(true);

      let assessmentId = assignmentData.selectedAssessmentId;

      // Create new assessment if needed
      if (assignmentData.assessmentType === 'new') {
        const newAssessment = {
          name: assignmentData.newAssessmentName,
          description: assignmentData.newAssessmentDescription,
          configuration: {
            total_duration_minutes: assignmentData.newAssessmentDuration,
            total_score: assignmentData.newAssessmentScore,
            sections: [
              {
                section_name: "Listening Section",  
                duration_minutes: 5,
                total_score: 30,
                question_selection: {
                  listening_comprehension_sets: 1,
                  difficulty_level: "intermediate"
                }
              },
              {
                section_name: "Reading Section",  
                duration_minutes: 5,
                total_score: 30,
                question_selection: {
                  reading_fluency_individual: 3,
                  difficulty_level: "intermediate"
                }
              },
              {
                section_name: "Speaking Section",  
                duration_minutes: 5,
                total_score: 40,
                question_selection: {
                  personal_background: 1,
                  opinion_reasoning: 1,
                  problem_solving: 1,
                  future_planning: 1
                }
              }
            ]
          },
          language_id: 1,
        };

        const assessmentResult = await apiUtils.post(API_ENDPOINTS.CREATE_ASSESSMENT, newAssessment);
        assessmentId = assessmentResult.data.id;
      }

      // Create assignment
    console.log('Available tenant access:', tenantAccess);
    console.log('Selected tenant ID:', assignmentData.selectedTenantId);
    
    const tenantAccessId = assignmentData.selectedTenantId || tenantAccess[0]?.id;
    console.log('Final tenant access ID:', tenantAccessId);
    
    if (!tenantAccessId) {
      alert('No tenant access available. Please ensure tenant access is configured.');
      return;
    }
    
    const assignmentPayload = {
      name: savedTalent.name,  // Matches database field
      email_id: savedTalent.email,  // Matches database field
      assessment_id: assessmentId,
      tenant_access_id: tenantAccessId,
      token: `token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,  // Generate unique token
    };
    
    console.log('Assignment payload:', assignmentPayload);

      await apiUtils.post(API_ENDPOINTS.CREATE_ASSIGNMENT, assignmentPayload);
      alert('Assignment created successfully!');
      handleCloseDialogs();
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error creating assignment:', error);
      alert('Error creating assignment');
    } finally {
      setAssignmentLoading(false);
    }
  };

  return (
    <Box p={4}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h3" fontWeight={800} className="gradient-text" mb={2}>
          📊 Assessment Dashboard
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage talents, assessments, and assignments
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box display="flex" gap={3} mb={4} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight={700}>{talents.length}</Typography>
                <Typography variant="body1">Total Talents</Typography>
              </Box>
              <Person sx={{ fontSize: 48, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight={700}>{assessments.length}</Typography>
                <Typography variant="body1">Assessments</Typography>
              </Box>
              <AssessmentIcon sx={{ fontSize: 48, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight={700}>{tenantAccess.length}</Typography>
                <Typography variant="body1">Tenants</Typography>
              </Box>
              <Business sx={{ fontSize: 48, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Add Talent Button */}
      <Box mb={4}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Add />}
          onClick={handleOpenDialog}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            px: 4,
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 600,
          }}
        >
          Add Talent & Assign Assessment
        </Button>
      </Box>

      {/* Recent Talents */}
      <Card>
        <CardContent>
          <Typography variant="h5" fontWeight={600} mb={3}>
            Recent Talents
          </Typography>
          {talents.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No talents added yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Add Talent & Assign Assessment" to get started
              </Typography>
            </Box>
          ) : (
            <Box>
              {talents.slice(0, 5).map((talent) => (
                <Box key={talent.id} display="flex" alignItems="center" gap={2} py={2} borderBottom="1px solid #eee">
                  <Avatar>
                    <Person />
                  </Avatar>
                  <Box flex={1}>
                    <Typography fontWeight={600}>{talent.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{talent.email}</Typography>
                  </Box>
                  <Chip
                    label={talent.status}
                    color={talent.status === 'completed' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Talent & Assign Assessment Dialog */}
      <Dialog
        open={assignAssessmentOpen}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Add />
            Add Talent & Assign Assessment
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box mb={3}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Step 1: Add Talent */}
          {activeStep === 0 && (
            <Box display="flex" flexDirection="column" gap={3}>
              <Typography variant="h6" mb={2}>
                👤 Add New Talent
              </Typography>
              
              <TextField
                label="Talent Name"
                value={assignmentData.talentName}
                onChange={(e) => setAssignmentData(prev => ({ ...prev, talentName: e.target.value }))}
                fullWidth
                required
              />
              
              <TextField
                label="Email Address"
                type="email"
                value={assignmentData.talentEmail}
                onChange={(e) => setAssignmentData(prev => ({ ...prev, talentEmail: e.target.value }))}
                fullWidth
                required
              />
            </Box>
          )}

          {/* Step 2: Assign Assessment */}
          {activeStep === 1 && savedTalent && (
            <Box display="flex" flexDirection="column" gap={3}>
              <Typography variant="h6" mb={2}>
                📋 Assign Assessment
              </Typography>
              
              <Box mb={3} p={2} bgcolor="grey.50" borderRadius={2}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Assigning to:
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography fontWeight={600}>{savedTalent.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {savedTalent.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />
              
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Assessment Type Selection */}
                <FormControl component="fieldset">
                  <FormLabel component="legend">Assessment Type</FormLabel>
                  <RadioGroup
                    value={assignmentData.assessmentType}
                    onChange={(e) => setAssignmentData(prev => ({ ...prev, assessmentType: e.target.value }))}
                  >
                    <FormControlLabel
                      value="existing"
                      control={<Radio />}
                      label="Use Existing Assessment"
                    />
                    <FormControlLabel
                      value="new"
                      control={<Radio />}
                      label="Create New Assessment"
                    />
                  </RadioGroup>
                </FormControl>

                {/* Existing Assessment Selection */}
                {assignmentData.assessmentType === 'existing' && (
                  <FormControl fullWidth required>
                    <InputLabel>Select Assessment *</InputLabel>
                    <Select
                      value={assignmentData.selectedAssessmentId}
                      onChange={(e) => setAssignmentData(prev => ({ ...prev, selectedAssessmentId: e.target.value }))}
                      label="Select Assessment *"
                      renderValue={(value) => {
                        const selected = assessments.find(a => a.id === value);
                        return selected ? `${selected.name} (${selected.total_duration_minutes} min, ${selected.total_score} pts)` : '';
                      }}
                    >
                      {assessments.map((assessment) => {
                        const config = assessment.configuration;
                        const sections = config?.sections || [];
                        const totalQuestions = sections.reduce((sum, section) => {
                          const questionCount = Object.values(section.question_selection || {}).reduce((a, b) => a + b, 0);
                          return sum + questionCount;
                        }, 0);

                        return (
                          <MenuItem key={assessment.id} value={assessment.id}>
                            <Box sx={{ width: '100%', py: 1 }}>
                              {/* Assessment Name & Totals */}
                              <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                                {assessment.name}
                              </Typography>
                              <Box display="flex" gap={2} sx={{ mb: 1.5 }}>
                                <Chip
                                  label={`${assessment.total_duration_minutes} min`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`${assessment.total_score} pts`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`${totalQuestions} questions`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              </Box>

                              {/* Section Breakdown */}
                              {sections.length > 0 && (
                                <Box sx={{ pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                                  {sections.map((section, idx) => {
                                    const questionCount = Object.values(section.question_selection || {}).reduce((a, b) => a + b, 0);
                                    const questionTypes = Object.entries(section.question_selection || {})
                                      .filter(([_, count]) => count > 0)
                                      .map(([type, count]) => `${type.replace(/_/g, ' ')} (${count})`)
                                      .join(', ');

                                    return (
                                      <Box key={idx} sx={{ mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                          <strong>{section.section_name}:</strong> {questionCount} questions • {section.duration_minutes} min • {section.total_score} pts
                                        </Typography>
                                        {questionTypes && (
                                          <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>
                                            {questionTypes}
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}

                {/* New Assessment Creation */}
                {assignmentData.assessmentType === 'new' && (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      label="Assessment Name"
                      value={assignmentData.newAssessmentName}
                      onChange={(e) => setAssignmentData(prev => ({ ...prev, newAssessmentName: e.target.value }))}
                      fullWidth
                      required
                    />
                    
                    <TextField
                      label="Assessment Description"
                      value={assignmentData.newAssessmentDescription}
                      onChange={(e) => setAssignmentData(prev => ({ ...prev, newAssessmentDescription: e.target.value }))}
                      fullWidth
                      multiline
                      rows={3}
                      required
                    />

                    {/* Dynamic Section Configuration */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                      📋 Section Configuration
                    </Typography>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Configure your assessment sections and subsections below. Set question counts and scores for each subsection.
                    </Alert>
                    
                    {sections.map((section, index) => {
                      const sectionConfig = sectionConfigs[section.id];
                      const sectionSubsections = subsections.filter(sub => sub.section_id === section.id);
                      const sectionInstructions = section.instructions || {};
                      const questionTypes = sectionInstructions.question_types || [];
                      
                      const borderColors = ['primary.light', 'success.light', 'warning.light', 'info.light'];
                      const iconColors = ['primary', 'success', 'warning', 'info'] as const;
                      const icons = [VolumeUp, MenuBook, Mic, AssessmentIcon];
                      const IconComponent = icons[index % icons.length];
                      
                      if (!sectionConfig) return null;
                      
                      return (
                        <Card key={section.id} sx={{ mb: 2, border: '1px solid', borderColor: borderColors[index % borderColors.length] }}>
                          <CardContent>
                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                              <IconComponent color={iconColors[index % iconColors.length]} />
                              <Typography variant="h6" fontWeight={600}>{section.name}</Typography>
                              <Box sx={{ ml: 'auto' }}>
                                <FormControlLabel
                                  control={
                                    <Switch 
                                      checked={sectionConfig.enabled}
                                      onChange={(e) => updateSectionEnabled(section.id, e.target.checked)}
                                    />
                                  }
                                  label="Enable Section"
                                />
                              </Box>
                            </Box>
                            
                            {section.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {section.description}
                              </Typography>
                            )}
                            
                            {/* Subsections or Question Types */}
                            {(sectionSubsections.length > 0 || questionTypes.length > 0) && (
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                                  {sectionSubsections.length > 0 ? 'Subsections:' : 'Question Types:'}
                                </Typography>
                                
                                {/* Real Subsections */}
                                {sectionSubsections.map((subsection) => {
                                  const subsectionConfig = sectionConfig.subsections[subsection.id];
                                  if (!subsectionConfig) return null;
                                  
                                  return (
                                    <Box key={subsection.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Checkbox
                                          checked={subsectionConfig.enabled}
                                          onChange={(e) => updateSubsectionConfig(section.id, subsection.id, 'enabled', e.target.checked)}
                                          size="small"
                                        />
                                        <Typography variant="body1" fontWeight={500} sx={{ flex: 1 }}>
                                          {subsection.name}
                                        </Typography>
                                      </Box>
                                      
                                      {subsectionConfig.enabled && (
                                        <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                                          <TextField
                                            label="Questions"
                                            type="number"
                                            value={subsectionConfig.questionCount}
                                            onChange={(e) => updateSubsectionConfig(section.id, subsection.id, 'questionCount', parseInt(e.target.value) || 0)}
                                            size="small"
                                            inputProps={{ min: 0, max: 20 }}
                                            sx={{ flex: 1 }}
                                          />
                                          <TextField
                                            label="Score"
                                            type="number"
                                            value={subsectionConfig.score}
                                            onChange={(e) => updateSubsectionConfig(section.id, subsection.id, 'score', parseInt(e.target.value) || 0)}
                                            size="small"
                                            inputProps={{ min: 0, max: 100 }}
                                            sx={{ flex: 1 }}
                                          />
                                        </Box>
                                      )}
                                      
                                      {subsection.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                          {subsection.description}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                                
                                {/* Virtual Question Types (when no real subsections) */}
                                {sectionSubsections.length === 0 && questionTypes.map((questionType) => {
                                  const virtualId = `${section.id}_${questionType}`;
                                  const subsectionConfig = sectionConfig.subsections[virtualId];
                                  if (!subsectionConfig) return null;
                                  
                                  return (
                                    <Box key={virtualId} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Checkbox
                                          checked={subsectionConfig.enabled}
                                          onChange={(e) => updateSubsectionConfig(section.id, virtualId, 'enabled', e.target.checked)}
                                          size="small"
                                        />
                                        <Typography variant="body1" fontWeight={500} sx={{ flex: 1 }}>
                                          {questionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </Typography>
                                      </Box>
                                      
                                      {subsectionConfig.enabled && (
                                        <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                                          <TextField
                                            label="Questions"
                                            type="number"
                                            value={subsectionConfig.questionCount}
                                            onChange={(e) => updateSubsectionConfig(section.id, virtualId, 'questionCount', parseInt(e.target.value) || 0)}
                                            size="small"
                                            inputProps={{ min: 0, max: 20 }}
                                            sx={{ flex: 1 }}
                                          />
                                          <TextField
                                            label="Score"
                                            type="number"
                                            value={subsectionConfig.score}
                                            onChange={(e) => updateSubsectionConfig(section.id, virtualId, 'score', parseInt(e.target.value) || 0)}
                                            size="small"
                                            inputProps={{ min: 0, max: 100 }}
                                            sx={{ flex: 1 }}
                                          />
                                        </Box>
                                      )}
                                      
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Question type: {questionType}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                    
                    <Box display="flex" gap={2}>
                      <TextField
                        label="Duration (minutes)"
                        type="number"
                        value={assignmentData.newAssessmentDuration}
                        onChange={(e) => setAssignmentData(prev => ({ ...prev, newAssessmentDuration: parseInt(e.target.value) }))}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Total Score"
                        type="number"
                        value={assignmentData.newAssessmentScore}
                        onChange={(e) => setAssignmentData(prev => ({ ...prev, newAssessmentScore: parseInt(e.target.value) }))}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          {activeStep === 0 ? (
            <Button 
              onClick={handleAddTalent} 
              variant="contained"
              disabled={assignmentLoading}
            >
              {assignmentLoading ? <CircularProgress size={20} /> : 'Add Talent'}
            </Button>
          ) : (
            <Button 
              onClick={handleAssignAssessment} 
              variant="contained"
              disabled={assignmentLoading}
            >
              {assignmentLoading ? <CircularProgress size={20} /> : 'Assign Assessment'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
